import { useMemo } from 'react';
import { useFetchUserData } from '../queries/auth/user';
import { NavPermissions } from '../config/permissions.config';
import type { AccessLevel } from '../config/permissions.config';
import { getBusinessType, getRoleAllowedFeatures, isRestrictedRole, isFeatureVisible } from '../utils/roleConfig';
import { FEATURE_KEYS } from '../utils/featureConstants';

export function usePermissions() {
  const { userData, isLoading } = useFetchUserData();

  const rawTypeString = useMemo(() => {
    if (!userData) return '';
    const user = userData?.user || userData;
    return String(
      userData?.business_type ||
      userData?.business_category ||
      userData?.shop?.business_type ||
      userData?.shop?.business_category ||
      userData?.shop?.category ||
      userData?.business_type_id ||
      userData?.shop?.business_type_id ||
      user?.business_type ||
      user?.business_category ||
      user?.shop?.business_type ||
      user?.shop?.business_category ||
      user?.shop?.category ||
      user?.business_type_id ||
      user?.shop?.business_type_id ||
      userData?.data?.business_type ||
      userData?.data?.business_category ||
      userData?.data?.shop?.business_type ||
      userData?.data?.shop?.business_category ||
      localStorage.getItem('business_type') ||
      localStorage.getItem('business_category') ||
      ''
    );
  }, [userData]);

  const businessType = useMemo(() => {
    return getBusinessType(rawTypeString);
  }, [rawTypeString]);

  const rawBusinessType = useMemo(() => {
    const raw = rawTypeString.toLowerCase();
    if (raw.includes('coffee') || raw === 'cafe') {
      return 'coffee_shop';
    }
    return raw;
  }, [rawTypeString]);

  const permissions = useMemo(() => {
    return userData?.permissions || [];
  }, [userData]);

  const isAdmin = useMemo(() => {
    if (userData?.is_super_admin) return true;
    
    const roleName = (userData?.user_role_name || userData?.user_role || '').toLowerCase();
    const isExplicitAdmin = userData?.is_admin === true || userData?.user?.user_role_id === 1;
    const isNamedAdmin = roleName.includes('administrator') || roleName.includes('owner') || roleName.includes('manager');
    
    return isExplicitAdmin || isNamedAdmin;
  }, [userData]);

  const userRole = userData?.user_role_name || userData?.user_role || 'unknown';

  const userRolesList = useMemo(() => {
    if (userData?.user_roles && Array.isArray(userData.user_roles)) {
      return userData.user_roles;
    }
    return userRole && userRole !== 'unknown' ? [userRole] : [];
  }, [userData, userRole]);

  const hasUnrestrictedRole = useMemo(() => {
    if (!userRolesList || userRolesList.length === 0) return false;
    return userRolesList.some((r: string) => !isRestrictedRole(r));
  }, [userRolesList]);

  const roleAllowedFeatures = useMemo(() => {
    let hasUnrestricted = false;
    let hasAnyRestrictions = false;
    const combinedFeatures = new Set<number>();
    
    userRolesList.forEach((r: string) => {
      const features = getRoleAllowedFeatures(r);
      if (features) {
        hasAnyRestrictions = true;
        features.forEach((f) => combinedFeatures.add(f));
      } else {
        hasUnrestricted = true;
      }
    });

    // If they have at least one unrestricted role, they are unrestricted (return null)
    if (hasUnrestricted && userRolesList.length > 0) {
      return null;
    }
    
    return hasAnyRestrictions ? combinedFeatures : null;
  }, [userRolesList]);

  const hasPermission = (featureKey: number): boolean => {
    if (userData?.is_super_admin) return true;

    // 0. Microservice Domain Dashboards & Features: Always allow matching active business vertical
    if (
      (businessType === 'shoe' && ([
        FEATURE_KEYS.SHOE_DASHBOARD,
        FEATURE_KEYS.SHOE_CATALOG,
        FEATURE_KEYS.SHOE_POS,
        FEATURE_KEYS.SHOE_PREPACKS,
        FEATURE_KEYS.SHOE_AUDITS,
        FEATURE_KEYS.SHOE_MISMATCHED,
        FEATURE_KEYS.SHOE_CARE,
        FEATURE_KEYS.SHOE_LABELS,
        FEATURE_KEYS.SHOE_SIZE_CONVERTER,
        FEATURE_KEYS.CUSTOMERS,
        FEATURE_KEYS.DEBTS,
        FEATURE_KEYS.REPORTS,
        FEATURE_KEYS.SALES_ANALYTICS,
        FEATURE_KEYS.SALES_HISTORY,
        FEATURE_KEYS.STOCK_MOVEMENTS,
        FEATURE_KEYS.SALES,
        FEATURE_KEYS.POS_V2,
        FEATURE_KEYS.DASHBOARD,
        FEATURE_KEYS.SUPPLIERS,
        FEATURE_KEYS.AUDIT_LOGS,
      ] as number[]).includes(featureKey)) ||
      (businessType === 'hardware' && ([
        FEATURE_KEYS.HARDWARE_DASHBOARD,
        FEATURE_KEYS.PRODUCTS,
        FEATURE_KEYS.INVENTORY,
        FEATURE_KEYS.SALES,
        FEATURE_KEYS.POS_V2,
        FEATURE_KEYS.CUSTOMERS,
        FEATURE_KEYS.DEBTS,
        FEATURE_KEYS.FUNDS,
        FEATURE_KEYS.EXPENSES,
        FEATURE_KEYS.REPORTS,
        FEATURE_KEYS.SALES_ANALYTICS,
        FEATURE_KEYS.SALES_HISTORY,
        FEATURE_KEYS.STOCK_MOVEMENTS,
        FEATURE_KEYS.DASHBOARD,
        FEATURE_KEYS.SUPPLIERS,
        FEATURE_KEYS.AUDIT_LOGS,
      ] as number[]).includes(featureKey)) ||
      (businessType === 'clothing' && featureKey === FEATURE_KEYS.CLOTHING_DASHBOARD) ||
      (businessType === 'grocery' && ([
        FEATURE_KEYS.GROCERY_DASHBOARD,
        FEATURE_KEYS.GROCERY_POS,
        FEATURE_KEYS.GROCERY_INVENTORY,
        FEATURE_KEYS.GROCERY_EXPIRIES,
        FEATURE_KEYS.GROCERY_SPOILAGE,
        FEATURE_KEYS.GROCERY_REPORTS,
        FEATURE_KEYS.GROCERY_PLU_SCALE,
        FEATURE_KEYS.CUSTOMERS,
        FEATURE_KEYS.SUPPLIERS,
        FEATURE_KEYS.REPORTS,
        FEATURE_KEYS.SALES_HISTORY,
        FEATURE_KEYS.SALES,
        FEATURE_KEYS.POS_V2,
        FEATURE_KEYS.DASHBOARD,
        FEATURE_KEYS.AUDIT_LOGS,
      ] as number[]).includes(featureKey)) ||
      (businessType === 'jewelry' && ([
        FEATURE_KEYS.JEWELRY_DASHBOARD,
        FEATURE_KEYS.JEWELRY_VAULT,
        FEATURE_KEYS.JEWELRY_POS,
        FEATURE_KEYS.JEWELRY_TRADE_INS,
        FEATURE_KEYS.JEWELRY_SPOT_RATES,
        FEATURE_KEYS.JEWELRY_CERTIFICATES,
        FEATURE_KEYS.JEWELRY_REPORTS,
        FEATURE_KEYS.CUSTOMERS,
        FEATURE_KEYS.REPORTS,
        FEATURE_KEYS.SALES_ANALYTICS,
        FEATURE_KEYS.SALES_HISTORY,
        FEATURE_KEYS.SALES,
        FEATURE_KEYS.POS_V2,
        FEATURE_KEYS.DASHBOARD,
        FEATURE_KEYS.SUPPLIERS,
        FEATURE_KEYS.AUDIT_LOGS,
      ] as number[]).includes(featureKey)) ||
      ((businessType === 'hotel' ||
        (businessType as string) === 'hotel & hospitality' ||
        (businessType as string).includes('hotel') ||
        (businessType as string).includes('hostel') ||
        (businessType as string).includes('hospitality') ||
        (businessType as string).includes('lodge') ||
        (businessType as string).includes('resort') ||
        (businessType as string).includes('motel') ||
        (businessType as string).includes('guesthouse') ||
        (businessType as string).includes('guest_house')) && ([
        FEATURE_KEYS.HOTEL_DASHBOARD,
        FEATURE_KEYS.HOTEL_ROOMS,
        FEATURE_KEYS.HOTEL_RESERVATIONS,
        FEATURE_KEYS.HOTEL_HOUSEKEEPING,
        FEATURE_KEYS.HOTEL_FOLIOS,
        FEATURE_KEYS.HOTEL_REPORTS,
        FEATURE_KEYS.HOTEL_GUESTS,
      ] as number[]).includes(featureKey)) ||
      (businessType === 'pharmacy' && featureKey === FEATURE_KEYS.PHARMACY_DASHBOARD) ||
      (businessType === 'hospital' && featureKey === FEATURE_KEYS.HOSPITAL_DASHBOARD) ||
      (businessType === 'mobile_money' && ([
        FEATURE_KEYS.MOBILE_MONEY_DASHBOARD,
        FEATURE_KEYS.MOBILE_MONEY_POS,
        FEATURE_KEYS.MOBILE_MONEY_FLOATS,
        FEATURE_KEYS.MOBILE_MONEY_LEDGER,
        FEATURE_KEYS.MOBILE_MONEY_RECONCILIATION,
        FEATURE_KEYS.MOBILE_MONEY_POS_OPS,
        FEATURE_KEYS.MOBILE_MONEY_SHIFT_RECONCILIATION,
        FEATURE_KEYS.MOBILE_MONEY_FLOAT_MGMT,
        FEATURE_KEYS.MOBILE_MONEY_FINANCIAL_ANALYTICS,
        FEATURE_KEYS.MOBILE_MONEY_EXPENSES_OUTFLOWS,
        FEATURE_KEYS.MOBILE_MONEY_TELLER_MGMT,
        FEATURE_KEYS.MOBILE_MONEY_SETTINGS_COMMISSIONS,
      ] as number[]).includes(featureKey)) ||
      (businessType === 'bar' && featureKey === FEATURE_KEYS.BAR_DASHBOARD) ||
      (businessType === 'restaurant' && featureKey === FEATURE_KEYS.RESTAURANT_DASHBOARD) ||
      (businessType === 'real_estate' && ([
        FEATURE_KEYS.REAL_ESTATE_DASHBOARD,
        FEATURE_KEYS.CUSTOMERS,
        FEATURE_KEYS.DEBTS,
        FEATURE_KEYS.EXPENSES,
        FEATURE_KEYS.FUNDS,
        FEATURE_KEYS.REPORTS,
      ] as number[]).includes(featureKey)) ||
      (businessType === 'barbershop' && ([
        FEATURE_KEYS.BARBERSHOP_DASHBOARD,
        FEATURE_KEYS.CUSTOMERS,
        FEATURE_KEYS.PRODUCTS,
        FEATURE_KEYS.EXPENSES,
        FEATURE_KEYS.FUNDS,
        FEATURE_KEYS.REPORTS,
      ] as number[]).includes(featureKey)) ||
      (businessType === 'spa' && ([
        FEATURE_KEYS.SPA_DASHBOARD,
        FEATURE_KEYS.CUSTOMERS,
        FEATURE_KEYS.PRODUCTS,
        FEATURE_KEYS.EXPENSES,
        FEATURE_KEYS.FUNDS,
        FEATURE_KEYS.REPORTS,
      ] as number[]).includes(featureKey)) ||
      (businessType === 'tailoring' && ([
        FEATURE_KEYS.TAILORING_DASHBOARD,
        FEATURE_KEYS.CUSTOMERS,
        FEATURE_KEYS.DEBTS,
        FEATURE_KEYS.EXPENSES,
        FEATURE_KEYS.FUNDS,
        FEATURE_KEYS.REPORTS,
      ] as number[]).includes(featureKey)) ||
      (businessType === 'freelancer' && ([
        FEATURE_KEYS.PROFESSIONAL_SERVICES_DASHBOARD,
        FEATURE_KEYS.CUSTOMERS,
        FEATURE_KEYS.DEBTS,
        FEATURE_KEYS.EXPENSES,
        FEATURE_KEYS.FUNDS,
        FEATURE_KEYS.REPORTS,
      ] as number[]).includes(featureKey)) ||
      (businessType === 'salon' && ([
        FEATURE_KEYS.SALON_DASHBOARD,
        FEATURE_KEYS.CUSTOMERS,
        FEATURE_KEYS.PRODUCTS,
        FEATURE_KEYS.EXPENSES,
        FEATURE_KEYS.FUNDS,
        FEATURE_KEYS.REPORTS,
      ] as number[]).includes(featureKey)) ||
      (businessType === 'car_rental' && ([
        FEATURE_KEYS.CAR_RENTAL_DASHBOARD,
        FEATURE_KEYS.CUSTOMERS,
        FEATURE_KEYS.DEBTS,
        FEATURE_KEYS.EXPENSES,
        FEATURE_KEYS.FUNDS,
        FEATURE_KEYS.REPORTS,
      ] as number[]).includes(featureKey))
    ) {
      return true;
    }

    // 1. Admin or Owner or Manager
    if (isAdmin) {
      return true;
    }

    // 2. Check permissions array
    if (Array.isArray(permissions) && permissions.includes(featureKey)) {
      return true;
    }

    // 3. Fallback to business-type feature visibility
    return isFeatureVisible('', featureKey, businessType);
  };

  const hasAccess = (navKey: string): boolean => {
    if (userData?.is_super_admin) return true;

    const config = NavPermissions[navKey];
    if (!config) return true; // Fail open for unknown nav keys

    return hasPermission(config.featureKey);
  };

  const getAccessLevel = (navKey: string): AccessLevel => {
    if (userData?.is_super_admin) return 'full';
    return hasAccess(navKey) ? 'full' : 'hidden';
  };

  return { hasAccess, hasPermission, getAccessLevel, userRole, userRolesList, isAdmin, businessType, rawBusinessType, roleAllowedFeatures, isLoading, userData };
}
